import type { AyantDroitStatut } from '@cpce/shared';
import { joinOptional } from '@cpce/shared';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Badge, BadgeColor, OptionBadge } from '@/components/Badge';
import { StyledLink } from '@/components/StyledLink';
import { createSelectColumn } from '@/components/table/select-column';
import type { BadgeOption } from '@/components/table/table.types';
import { RELATIONSHIP_OPTIONS } from '@/features/beneficiaries/employees/employees.constants';
import { formatDate } from '@/utils/date.utils';
import type { BeneficiaryWithEmployeesFormatted } from '../../beneficiaries.types';
import { createProfileColumn } from '../../profile-column';

export const AYANT_DROIT_STATUT_OPTIONS: BadgeOption<AyantDroitStatut>[] = [
  { value: 'En attente', label: 'En attente', color: BadgeColor.ORANGE },
  { value: 'Accepté', label: 'Accepté', color: BadgeColor.GREEN },
  { value: 'Rejeté', label: 'Rejeté', color: BadgeColor.RED },
  { value: 'Document invalide', label: 'Document invalide', color: BadgeColor.RED },
];

export const A_CHARGE_OPTIONS: BadgeOption<boolean>[] = [
  { value: true, label: 'À charge', color: BadgeColor.GREEN },
  { value: false, label: 'Non à charge', color: BadgeColor.GRAY },
];

export function ACharge(props: { value: boolean | null }) {
  const { value } = props;
  if (value === null) return <span>-</span>;
  const option = A_CHARGE_OPTIONS.find((o) => o.value === value);
  return <Badge color={option?.color}>{option?.label}</Badge>;
}

export const ACTIVITY_STATUT_OPTIONS: BadgeOption<boolean>[] = [
  { value: true, label: 'Actif', color: BadgeColor.GREEN },
  { value: false, label: 'Archivé', color: BadgeColor.DARK_GRAY },
];

const columnHelper = createColumnHelper<BeneficiaryWithEmployeesFormatted>();

interface EmployeesBeneficiariesColumnsOptions {
  commiteeSlug: string;
}

export function getEmployeesBeneficiariesColumns(
  options: EmployeesBeneficiariesColumnsOptions
): ColumnDef<BeneficiaryWithEmployeesFormatted>[] {
  const { commiteeSlug } = options;

  return [
    createSelectColumn<BeneficiaryWithEmployeesFormatted>(),
    columnHelper.accessor('lastName', {
      header: 'Nom',
      meta: { filterType: 'text' },
    }),
    columnHelper.accessor('firstName', {
      header: 'Prénom',
      meta: { filterType: 'text' },
    }),
    columnHelper.accessor((row) => row.birthDate, {
      id: 'birthDate',
      header: 'Date de naissance',
      cell: (info) => formatDate(info.getValue()) ?? '-',
      meta: { filterType: 'numeric' },
    }),
    columnHelper.accessor(
      (row) =>
        joinOptional(
          row.employeeBeneficiaries.map(
            (employeeBeneficiary) => employeeBeneficiary.employee.beneficiary.fullName
          )
        ),
      {
        id: 'attachedEmployee',
        header: 'Salariés rattachés',
        cell: ({ row }) => {
          return (
            <div className="flex gap-2">
              {row.original.employeeBeneficiaries.map((employeeBeneficiary) => (
                <StyledLink
                  key={employeeBeneficiary.employee.id}
                  navigateTo={{
                    to: '/$commiteeSlug/beneficiaries/employees/{-$id}',
                    params: { commiteeSlug, id: employeeBeneficiary.employee.id.toString() },
                  }}
                  text={employeeBeneficiary.employee.beneficiary.fullName ?? '-'}
                />
              ))}
            </div>
          );
        },
        meta: { filterType: 'text' },
      }
    ),
    columnHelper.accessor((row) => row.employeeBeneficiaries[0]?.status ?? null, {
      id: 'relationship',
      header: 'Lien',
      cell: (info) => <OptionBadge options={RELATIONSHIP_OPTIONS} value={info.getValue()} />,
      meta: { filterType: 'badge-enum', options: RELATIONSHIP_OPTIONS },
    }),
    columnHelper.accessor('aCharge', {
      header: 'À charge',
      cell: (info) => <ACharge value={info.getValue()} />,
      meta: { filterType: 'badge-enum', options: A_CHARGE_OPTIONS },
    }),
    // The id of the former Validation column, so filters users saved on it still apply.
    createProfileColumn<BeneficiaryWithEmployeesFormatted>({
      id: 'statut',
      header: 'Statut',
      facets: [
        { group: 'Validation', options: AYANT_DROIT_STATUT_OPTIONS, value: (row) => row.statut },
        {
          group: 'État',
          options: ACTIVITY_STATUT_OPTIONS,
          value: (row) => row.isActive,
          isShown: (row) => !row.isActive,
        },
      ],
    }),
  ] as ColumnDef<BeneficiaryWithEmployeesFormatted>[];
}
